import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { BaseStore } from './baseStore'
import { useCostTypeStore } from './costType'
import type { CostType } from './costType'
import { where, type QueryConstraint } from 'firebase/firestore'

export interface CostCenter {
  id?: string
  organizationId: string
  name: string
  code: string
  description?: string
  parent?: string
  active?: boolean
  createdAt?: string
  updatedAt?: string
  types?: CostType[]
}

class CostCenterStore extends BaseStore {
  constructor() {
    super('cost-centers')
  }

  async fetchCostCenters(organizationId: string) {
    if (!organizationId) {
      throw new Error('organizationId is required')
    }
    const filters: QueryConstraint[] = [
      where('organizationId', '==', organizationId)
    ]
    return await this.fetchCollection<CostCenter>(filters)
  }

  async addCostCenter(costCenter: CostCenter) {
    return await this.addDocument<CostCenter>(costCenter)
  }

  async updateCostCenter(id: string, costCenter: Partial<CostCenter>) {
    return await this.updateDocument<CostCenter>(id, costCenter)
  }

  async deleteCostCenter(id: string) {
    return await this.deleteDocument(id)
  }
}

export const useCostCenterStore = defineStore('costCenter', () => {
  const costCenterStore = new CostCenterStore()
  const costTypeStore = useCostTypeStore()
  const costCenters = ref<CostCenter[]>([])
  const loading = ref(false)

  const fetchCostCenters = async (organizationId: string) => {
    if (!organizationId) {
      console.error('organizationId is required')
      return []
    }

    loading.value = true
    try {
      console.log('Fetching centers for org:', organizationId)
      const centers = await costCenterStore.fetchCostCenters(organizationId)
      console.log('Fetched centers:', centers)
      
      if (!centers) {
        console.error('No centers returned from fetchCostCenters')
        return []
      }
      
      costCenters.value = centers
      
      // Fetch cost types for each center
      await Promise.all(costCenters.value.map(async (center) => {
        if (center.id) {
          try {
            const types = await costTypeStore.fetchCostTypesForCenter(organizationId, center.id)
            center.types = types || []
            console.log(`Types for center ${center.id}:`, types)
          } catch (error) {
            console.error(`Error fetching cost types for center ${center.id}:`, error)
            center.types = []
          }
        }
      }))

      return centers
    } catch (error) {
      console.error('Erro ao buscar centros de custo:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const addCostCenter = async (costCenter: Omit<CostCenter, 'id' | 'active' | 'createdAt' | 'updatedAt'>) => {
    if (!costCenter.organizationId) {
      throw new Error('organizationId is required')
    }

    try {
      const newCostCenter = await costCenterStore.addCostCenter({
        ...costCenter,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Adiciona o novo centro de custo ao array local
      costCenters.value = [...costCenters.value, newCostCenter]

      return newCostCenter
    } catch (error) {
      console.error('Erro ao adicionar centro de custo:', error)
      throw error
    }
  }

  const updateCostCenter = async (id: string, costCenter: Partial<CostCenter>) => {
    try {
      await costCenterStore.updateCostCenter(id, {
        ...costCenter,
        updatedAt: new Date().toISOString()
      })

      const index = costCenters.value.findIndex(cc => cc.id === id)
      if (index !== -1) {
        // Only update if it belongs to the same organization
        if (costCenter.organizationId && costCenters.value[index].organizationId !== costCenter.organizationId) {
          throw new Error('Não é possível atualizar centro de custo de outra organização')
        }
        const updatedCenter = { ...costCenters.value[index], ...costCenter }
        costCenters.value[index] = updatedCenter
        // Force Vue to detect the change
        costCenters.value = [...costCenters.value]
        return true
      }
      return false
    } catch (error) {
      console.error('Erro ao atualizar centro de custo:', error)
      throw error
    }
  }

  const deleteCostCenter = async (id: string) => {
    try {
      // Encontra o centro de custo e seus tipos
      const center = costCenters.value.find(cc => cc.id === id)
      if (!center) {
        throw new Error('Centro de custo não encontrado')
      }

      // Exclui todos os tipos de custo associados primeiro
      if (center.types && center.types.length > 0) {
        await Promise.all(
          center.types.map(type => costTypeStore.deleteCostType(type.id!))
        )
      }

      // Depois exclui o centro de custo
      await costCenterStore.deleteCostCenter(id)
      
      // Atualiza o estado local após deletar com sucesso
      costCenters.value = costCenters.value.filter(cc => cc.id !== id)
      
      return true
    } catch (error) {
      console.error('Erro ao excluir centro de custo:', error)
      throw new Error('Não foi possível excluir o centro de custo. Verifique se não há registros vinculados.')
    }
  }

  const getCostTypesForCenter = async (organizationId: string, centerId: string) => {
    if (!organizationId || !centerId) {
      throw new Error('organizationId and centerId are required')
    }
    try {
      return await costTypeStore.fetchCostTypesForCenter(organizationId, centerId)
    } catch (error) {
      console.error('Erro ao buscar tipos de custo do centro:', error)
      throw error
    }
  }

  const addCostType = async (centerId: string, costType: Omit<CostType, 'id' | 'costCenterId' | 'organizationId' | 'active' | 'createdAt' | 'updatedAt'>) => {
    if (!centerId) {
      throw new Error('centerId is required')
    }

    try {
      const center = costCenters.value.find(cc => cc.id === centerId)
      if (!center) {
        throw new Error('Centro de custo não encontrado')
      }

      // Verifica se já existe um tipo com o mesmo código no mesmo centro de custo
      const typesInCenter = await costTypeStore.fetchCostTypesForCenter(center.organizationId, centerId)
      const existingTypeInCenter = typesInCenter.find(t => t.code === costType.code)
      if (existingTypeInCenter) {
        throw new Error('Já existe um tipo de custo com este código neste centro de custo')
      }

      const newCostType = await costTypeStore.addCostType({
        ...costType,
        costCenterId: centerId,
        organizationId: center.organizationId
      })

      if (!newCostType) {
        throw new Error('Erro ao adicionar tipo de custo')
      }

      // Atualiza o estado local imediatamente
      const centerIndex = costCenters.value.findIndex(cc => cc.id === centerId)
      if (centerIndex !== -1) {
        const updatedCenter = { ...costCenters.value[centerIndex] }
        if (!updatedCenter.types) {
          updatedCenter.types = []
        }
        updatedCenter.types.push(newCostType)
        costCenters.value[centerIndex] = updatedCenter
        // Força a atualização do estado
        costCenters.value = [...costCenters.value]
      }

      return true
    } catch (error) {
      console.error('Erro ao adicionar tipo de custo:', error)
      throw error
    }
  }

  const updateCostType = async (centerId: string, typeId: string, costType: Partial<CostType>) => {
    try {
      const center = costCenters.value.find(cc => cc.id === centerId)
      if (!center) {
        throw new Error('Centro de custo não encontrado')
      }

      const existingType = center.types?.find(t => t.id === typeId)
      if (!existingType) {
        throw new Error('Tipo de custo não encontrado')
      }

      await costTypeStore.updateCostType(typeId, {
        ...costType,
        updatedAt: new Date().toISOString()
      })

      // Atualiza o estado local
      const centerIndex = costCenters.value.findIndex(cc => cc.id === centerId)
      if (centerIndex !== -1) {
        const updatedCenter = { ...costCenters.value[centerIndex] }
        if (updatedCenter.types) {
          const typeIndex = updatedCenter.types.findIndex(t => t.id === typeId)
          if (typeIndex !== -1) {
            updatedCenter.types[typeIndex] = { ...updatedCenter.types[typeIndex], ...costType }
            costCenters.value[centerIndex] = updatedCenter
            costCenters.value = [...costCenters.value]
            return true
          }
        }
      }
      return false
    } catch (error) {
      console.error('Erro ao atualizar tipo de custo:', error)
      throw error
    }
  }

  const deleteCostType = async (centerId: string, typeId: string) => {
    if (!centerId || !typeId) {
      throw new Error('centerId e typeId são obrigatórios')
    }

    try {
      // Verifica se o tipo existe antes de tentar excluir
      const center = costCenters.value.find(cc => cc.id === centerId)
      if (!center || !center.types) {
        throw new Error('Centro de custo ou tipo não encontrado')
      }

      const typeExists = center.types.some(t => t.id === typeId)
      if (!typeExists) {
        throw new Error('Tipo de custo não encontrado')
      }

      await costTypeStore.deleteCostType(typeId)

      // Atualiza o estado local imediatamente
      const centerIndex = costCenters.value.findIndex(cc => cc.id === centerId)
      if (centerIndex !== -1) {
        const updatedCenter = { ...costCenters.value[centerIndex] }
        updatedCenter.types = updatedCenter.types?.filter(t => t.id !== typeId) || []
        costCenters.value[centerIndex] = updatedCenter
        // Força a atualização do estado
        costCenters.value = [...costCenters.value]
      }

      return true
    } catch (error) {
      console.error('Erro ao excluir tipo de custo:', error)
      throw error
    }
  }

  return {
    costCenters,
    loading,
    fetchCostCenters,
    addCostCenter,
    updateCostCenter,
    deleteCostCenter,
    getCostTypesForCenter,
    addCostType,
    updateCostType,
    deleteCostType
  }
})